import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { PrismBanner, PrismButton } from "@prism-ui/react";
import { toast } from "../../../Toaster/toast";
import { notifyRatingWindowChanged } from "../../../../utils/ratingWindow";

export interface ChapterWindow {
  chapter_id: number;
  chapter_name: string;
  window_start: string | null;
  window_end: string | null;
  open?: boolean;
}

// "in 3 days", "tomorrow", "2 hours ago": the pickers already show the exact time.
export const relativeTime = (target: Date, now = new Date()): string => {
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const minutes = (target.getTime() - now.getTime()) / 60000;
  if (Math.abs(minutes) < 60) return rtf.format(Math.round(minutes), "minute");
  if (Math.abs(minutes) < 60 * 22) return rtf.format(Math.round(minutes / 60), "hour");
  return rtf.format(Math.round(minutes / (60 * 24)), "day");
};

// The browser's IANA zone plus its short name, e.g. "America/Denver (MDT)".
export const browserTimeZone = (now = new Date()): string => {
  const iana = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const short = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(now)
    .find((p) => p.type === "timeZoneName")?.value;
  return short && short !== iana ? `${iana} (${short})` : iana;
};

// Rating is open only between the saved dates; this mirrors the server's isRatingOpen.
export const windowStatus = (row: ChapterWindow, now = new Date()): { label: string; tone: string } => {
  if (!row.window_start || !row.window_end) return { label: "Closed: no dates", tone: "closed" };
  const start = new Date(row.window_start);
  const end = new Date(row.window_end);
  if (now < start) return { label: `Opens ${relativeTime(start, now)}`, tone: "scheduled" };
  if (now > end) return { label: `Ended ${relativeTime(end, now)}`, tone: "closed" };
  return { label: `🟢 Open, closes ${relativeTime(end, now)}`, tone: "open" };
};

/**
 * Reads a typed time of day: "9", "9:30", "930", "9:30p", "9.30 pm", "21:30", "noon", "midnight".
 * Returns "HH:mm", "" for blank, or null when it can't be read. Without am/pm, 0-23 is 24-hour time.
 */
export const parseTime = (text: string): string | null => {
  const t = text.trim().toLowerCase().replace(/[\s.]/g, "");
  if (!t) return "";
  if (t === "noon") return "12:00";
  if (t === "midnight") return "00:00";
  const m = /^(\d{1,2})(?::(\d{2}))?(a|am|p|pm)?$/.exec(t) ?? /^(\d{1,2})(\d{2})(a|am|p|pm)?$/.exec(t);
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = Number(m[2] ?? 0);
  const meridiem = m[3]?.[0];
  if (minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (meridiem === "p" ? 12 : 0);
  } else if (hour > 23) {
    return null;
  }
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

// "21:30" -> "9:30 PM"
export const formatTime = (hhmm: string): string => {
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

const TIME_SLOTS = Array.from({ length: 48 }, (_, i) =>
  formatTime(`${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`)
);
const TIME_LIST_ID = "rating-window-times";

// A whole-day default when a date is picked before a time.
const DEFAULT_TIME = { start: "00:00", end: "23:59" } as const;

// time: "HH:mm", "" when blank, or null while the typed text can't be read.
type Part = { date: string; time: string | null };
type Edit = { start: Part; end: Part };

const toParts = (iso: string | null): Part => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

const sameParts = (a: Part | undefined, b: Part) => !!a && a.date === b.date && a.time === b.time;

// Free-typed time with a 30-minute suggestion list; normalised to "9:30 AM" when it loses focus.
const TimeField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (next: string | null) => void;
}) => {
  const [draft, setDraft] = useState(value ? formatTime(value) : "");

  // Follow outside changes (a reload after saving, a default filled in by the date) without
  // fighting what is being typed: only reset when the draft no longer reads as the value.
  useEffect(() => {
    if (value !== null && parseTime(draft) !== value) setDraft(value ? formatTime(value) : "");
  }, [value, draft]);

  const invalid = value === null;
  return (
    <span className="rating-window-time-wrap">
      <input
        type="text"
        inputMode="text"
        className={`rating-window-time${invalid ? " invalid" : ""}`}
        aria-label={label}
        aria-invalid={invalid || undefined}
        placeholder="9:00 AM"
        list={TIME_LIST_ID}
        autoComplete="off"
        value={draft}
        onChange={(ev) => {
          setDraft(ev.target.value);
          onChange(parseTime(ev.target.value));
        }}
        onBlur={() => {
          const parsed = parseTime(draft);
          if (parsed) setDraft(formatTime(parsed));
        }}
      />
      {invalid && <small className="rating-window-time-hint">Try 9:30 AM or 21:30</small>}
    </span>
  );
};

export const apiError = (err: unknown, fallback: string): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  return e?.response?.data?.error || e?.message || fallback;
};

interface Props {
  rows: ChapterWindow[];
  onChanged: () => void;
  // Reports whether any row has edits that haven't been saved, so the pane can warn before leaving.
  onDirtyChange?: (dirty: boolean) => void;
}

const rowDirty = (edit: Edit | undefined, row: ChapterWindow) =>
  !!edit && (!sameParts(edit.start, toParts(row.window_start)) || !sameParts(edit.end, toParts(row.window_end)));

const RatingWindowTab = ({ rows, onChanged, onDirtyChange }: Props) => {
  const [edits, setEdits] = useState<Record<number, Edit>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const previousRows = useRef<ChapterWindow[]>([]);
  const justSaved = useRef<number | null>(null);

  // Saving one row reloads them all; keep other rows' unsaved edits unless the server changed that row.
  useEffect(() => {
    const before = previousRows.current;
    const saved = justSaved.current;
    previousRows.current = rows;
    justSaved.current = null;
    setEdits((current) => {
      const next: Record<number, Edit> = {};
      rows.forEach((r) => {
        const old = before.find((b) => b.chapter_id === r.chapter_id);
        const unchanged = old && old.window_start === r.window_start && old.window_end === r.window_end;
        next[r.chapter_id] =
          unchanged && r.chapter_id !== saved && rowDirty(current[r.chapter_id], old)
            ? current[r.chapter_id]
            : { start: toParts(r.window_start), end: toParts(r.window_end) };
      });
      return next;
    });
  }, [rows]);

  const save = async (row: ChapterWindow) => {
    const e = edits[row.chapter_id];
    if (!e) return;
    if (e.start.time === null || e.end.time === null) {
      toast.error("Enter a time like 9:30 AM or 21:30.");
      return;
    }
    if (!e.start.date || !e.start.time || !e.end.date || !e.end.time) {
      toast.error("Choose a date and time for both Opens and Closes.");
      return;
    }
    setBusy(row.chapter_id);
    try {
      await axios.put(`/api/rating_windows/${row.chapter_id}`, {
        window_start: new Date(`${e.start.date}T${e.start.time}`).toISOString(),
        window_end: new Date(`${e.end.date}T${e.end.time}`).toISOString(),
      });
      toast.success(`Rating window saved for ${row.chapter_name}.`);
      notifyRatingWindowChanged(row.chapter_id);
      justSaved.current = row.chapter_id;
      onChanged();
    } catch (err) {
      toast.error(apiError(err, "Unable to save rating window"));
    } finally {
      setBusy(null);
    }
  };

  const isDirty = (row: ChapterWindow) => rowDirty(edits[row.chapter_id], row);
  const dirtyRows = rows.filter(isDirty);
  const anyDirty = dirtyRows.length > 0;

  useEffect(() => {
    onDirtyChange?.(anyDirty);
  }, [anyDirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const update = (row: ChapterWindow, field: "start" | "end", patch: Partial<Part>) =>
    setEdits((all) => {
      const current = all[row.chapter_id] ?? { start: toParts(null), end: toParts(null) };
      return { ...all, [row.chapter_id]: { ...current, [field]: { ...current[field], ...patch } } };
    });

  const picker = (row: ChapterWindow, field: "start" | "end") => {
    const part = edits[row.chapter_id]?.[field] ?? { date: "", time: "" };
    const name = `${row.chapter_name} ${field === "start" ? "opens" : "closes"}`;
    return (
      <div className="rating-window-picker">
        <input
          type="date"
          aria-label={`${name} date`}
          value={part.date}
          onChange={(ev) =>
            update(row, field, {
              date: ev.target.value,
              ...(ev.target.value && part.time === "" ? { time: DEFAULT_TIME[field] } : {}),
            })
          }
        />
        <TimeField label={`${name} time`} value={part.time} onChange={(time) => update(row, field, { time })} />
      </div>
    );
  };

  return (
    <div>
      <PrismBanner tone="info">
        Raters can rate a chapter only between its <strong>Opens</strong> and <strong>Closes</strong> times. With
        no dates, rating is closed. To close rating early, set Closes to now; completing a formal Host Review also
        ends the window.
        <br />
        These times also control the <strong>Application Rater</strong> link in each rater&apos;s personalized
        menu: it appears when the window opens and disappears when it closes, without a page refresh.
      </PrismBanner>
      {anyDirty && (
        <p className="rating-window-unsaved" role="status">
          Unsaved changes for <strong>{dirtyRows.map((r) => r.chapter_name).join(", ")}</strong>. Save each row
          before leaving this tab.
        </p>
      )}
      <datalist id={TIME_LIST_ID}>
        {TIME_SLOTS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      <table className="application-rater-table rating-window-table">
        <thead>
          <tr>
            <th>Chapter</th>
            <th>Opens</th>
            <th>Closes</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const status = windowStatus(row);
            const dirty = isDirty(row);
            return (
              <tr key={row.chapter_id} className={dirty ? "rating-window-row-dirty" : undefined}>
                <td>
                  <strong>{row.chapter_name}</strong>
                  <small className={`rating-window-status ${status.tone}`} data-testid={`status-${row.chapter_id}`}>
                    {status.label}
                  </small>
                  {dirty && <small className="rating-window-dirty-tag">● Unsaved</small>}
                </td>
                <td>{picker(row, "start")}</td>
                <td>{picker(row, "end")}</td>
                <td>
                  <PrismButton
                    size="sm"
                    display={dirty ? "fill" : "outline"}
                    disabled={busy === row.chapter_id || !dirty}
                    onPrismClick={() => save(row)}
                  >
                    Save dates
                  </PrismButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="rating-window-zone">Times are in your time zone, {browserTimeZone()}.</p>
    </div>
  );
};

export default RatingWindowTab;
